import {EventEmitter, Injectable} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, UntypedFormControl, AbstractControl} from '@angular/forms';

/**
 * This class is responsible for conversion of an Object to a nested formgroup and setting of validation rules
 */
@Injectable()
export class FormBuilderService {
  public showValidationErrors: EventEmitter<any>;

  constructor(private fb: UntypedFormBuilder) {
    this.showValidationErrors = new EventEmitter();
  }

  public convertObjectToFormGroup(objData): UntypedFormGroup {
    delete objData.component;
    const formGroup = this.fb.group(this.removeArraysAndReturnClone(objData));
    this.convertGroups(objData, formGroup);
    return formGroup;
  }

  public mergeDeep(target, source) {
    const output = Object.assign({}, target);
    if (this.isObject(target) && this.isObject(source)) {
      Object.keys(source).forEach(key => {
        // console.log(key);
        if (this.isObject(source[key])) {
          if (!(key in target)) {
            Object.assign(output, {[key]: source[key]});
          } else {
            output[key] = this.mergeDeep(target[key], source[key]);
          }
        } else {
          Object.assign(output, {[key]: source[key]});
        }
      });
    }
    return output;
  }

  // this will remove all arrays because errors are caused when arrays passed to fb.group
  private removeArraysAndReturnClone(item) {
    const newObj = JSON.parse(JSON.stringify(item));
    Object.keys(newObj).forEach(key => {
      if ('value' in newObj[key]) {
        newObj[key] = newObj[key].value;
      }

      if (this.isArray(newObj[key])) {
        delete(newObj[key]);
      }
    });

    return newObj;
  }

  private convertGroups(obj, formGroup) {
    Object.keys(obj).map((key) => {
      if (this.isObject(obj[key])) {
        if (!('value' in obj[key])) {
          const newObject = this.removeArraysAndReturnClone(obj[key]);
          const newGroup = this.fb.group(newObject);
          formGroup.setControl(key, newGroup);
          this.convertGroups(obj[key], newGroup);
        }
      } else if (this.isArray(obj[key])) {
        const arrayContents = obj[key].map((item) => {
          const newGroup = this.fb.group(this.removeArraysAndReturnClone(item));
          this.convertGroups(item, newGroup);
          return newGroup;
        });
        const newGroup = this.fb.array(arrayContents);
        formGroup.setControl(key, newGroup);
      } else {
        formGroup.setControl(key, new UntypedFormControl(obj[key].value));
      }
    });
  }

  public setValidationRules(myModel, form) {
    const recursive = (model, baseForm) => {
      Object.keys(model).forEach(function (parentKey) {
        const parentObject = model[parentKey];
        const fc = baseForm.get(parentKey) as UntypedFormControl;
        if (fc && parentObject && parentObject.validation) {
          fc.setValidators(parentObject.validation);
        } else if (fc && parentObject && parentObject.validationAsync) {
          fc.setAsyncValidators(parentObject.validationAsync);
        } else if (fc) {
          recursive(parentObject, fc);
        }
      });
    };
    recursive(myModel, form);
  }

  public validateAllFormFields(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control: any = formGroup.get(field);
      if (control instanceof UntypedFormControl) {
        control.markAsTouched({ onlySelf: true });
      } else if (control instanceof UntypedFormGroup) {
        this.validateAllFormFields(control);
      } else {
        this.validateAllFormFields(control);
      }
    });
  }

  public setFormGroupTouched(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      control.markAsTouched({onlySelf: true});
    });
  }

  public isArray = function (a) {
    return (!!a) && (a.constructor === Array);
  };

  public isObject = function (a) {
    return (!!a) && (a.constructor === Object);
  };

  public getAllInvalidFields(form) {
    const invalid = [];

    const recursiveGetInvalidPath = (abstractFc, curPath) => {
      if (abstractFc.invalid && !abstractFc.controls) {
        invalid.push(curPath);
      } else if (abstractFc.invalid && abstractFc.controls) {
        getInvalidControls(abstractFc.controls).map((f) => {
          recursiveGetInvalidPath(f, f._controlName);
        });
      }
    };

    const getInvalidControls = (abstractFc: AbstractControl) => {
      const invalidControls = [];
      for (const i in abstractFc) {
        if (abstractFc[i] && abstractFc[i].invalid) {
          abstractFc[i]._controlName  = i;
          if (abstractFc[i].markAsTouched) {
            abstractFc[i].markAsTouched();
          }
          invalidControls.push(abstractFc[i]);
        }
      }
      // console.log(invalidControls);
      return invalidControls;
    };

    const filteredControls = getInvalidControls(form.controls).map((f, idx) => {
      recursiveGetInvalidPath(f, f._controlName);
    });

    return invalid;
  }

}
